import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { listOrganizations, createOrganization, updateOrganization } from "../../api/admin";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Plus, PencilSimple, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { isSuperAdminRole } from "../../utils/roles";
import { format } from "date-fns";

/**
 * AdminOrganizationsPage - Organizations management (Create, Read, Update — no Delete).
 * Super_admin only. Deactivating an org cascades to inactivate its users (except
 * super_admin/admin accounts); reactivating restores only those auto-deactivated users.
 */
export default function AdminOrganizationsPage() {
  const { user: currentUser } = useAuth();

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create/Edit modal state (shared dialog, `editingOrg` null = create mode)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: "", status: "active" });

  useEffect(() => {
    loadOrgs();
  }, []);

  const loadOrgs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listOrganizations();
      setOrgs(data);
    } catch (err) {
      console.error("Failed to load organizations:", err);
      setError(err?.response?.data?.detail || "Failed to load organizations");
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingOrg(null);
    setFormData({ name: "", status: "active" });
    setDialogOpen(true);
  };

  const openEditDialog = (org) => {
    setEditingOrg(org);
    setFormData({ name: org.name, status: org.status || "active" });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingOrg(null);
  };

  const handleSave = async () => {
    const name = formData.name.trim();
    if (!name) {
      toast.error("Organization name is required.");
      return;
    }
    setSaving(true);
    try {
      if (editingOrg) {
        const result = await updateOrganization(editingOrg.id, {
          name,
          status: formData.status,
        });
        setOrgs((prev) =>
          prev.map((o) =>
            o.id === editingOrg.id
              ? { ...o, name: result.name, status: result.status } // preserve user_count/usage stats from the last full list fetch
              : o
          )
        );
        if (result.cascade_affected_users > 0) {
          toast.success(
            formData.status === "inactive"
              ? `Organization deactivated — ${result.cascade_affected_users} user(s) lost access.`
              : `Organization reactivated — ${result.cascade_affected_users} user(s) restored.`
          );
        } else {
          toast.success("Organization updated.");
        }
      } else {
        const result = await createOrganization({ name, status: formData.status });
        setOrgs((prev) => [...prev, result]);
        toast.success(`Organization "${result.name}" created.`);
      }
      setDialogOpen(false);
      setEditingOrg(null);
    } catch (err) {
      console.error("Failed to save organization:", err);
      toast.error(err?.response?.data?.detail || "Failed to save organization.");
    } finally {
      setSaving(false);
    }
  };

  // Extra safety net — nav item is hidden for non-super-admins, but guard the
  // route directly too in case someone navigates here by URL.
  if (currentUser && !isSuperAdminRole(currentUser.role)) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-w-0 space-y-6" data-testid="admin-organizations-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-cabinet text-lg font-black tracking-tight text-slate-900">
            Organizations
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage tenant organizations. Deactivating an organization revokes access for its users.
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          size="sm"
          className="rounded-sm font-semibold text-xs"
          data-testid="create-org-btn"
        >
          <Plus size={16} />
          New Organization
        </Button>
      </div>

      <Card className="min-w-0 overflow-hidden rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Buildings size={16} />
            All Organizations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : orgs.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No organizations yet.</p>
          ) : (
            <Table
              className="min-w-[960px]"
              containerTestId="organizations-table-scroll"
              data-testid="organizations-table"
            >
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Total Tokens</TableHead>
                  <TableHead>Input / Output Tokens</TableHead>
                  <TableHead>LLM Calls</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgs.map((org) => (
                  <TableRow key={org.id} data-testid={`org-row-${org.id}`}>
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell>
                      <Badge
                        variant={org.status === "active" ? "default" : "destructive"}
                        className={`rounded-sm ${
                          org.status === "active"
                            ? "bg-green-600 hover:bg-green-700"
                            : "bg-red-600 hover:bg-red-700"
                        }`}
                        data-testid={`org-status-badge-${org.id}`}
                      >
                        {org.status || "active"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700" data-testid={`org-member-count-${org.id}`}>
                      {org.user_count ?? 0}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700 font-mono" data-testid={`org-total-tokens-${org.id}`}>
                      {(org.total_tokens ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 font-mono" data-testid={`org-io-tokens-${org.id}`}>
                      {(org.input_tokens ?? 0).toLocaleString()} / {(org.output_tokens ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700 font-mono" data-testid={`org-llm-calls-${org.id}`}>
                      {(org.llm_calls ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {org.created_at ? format(new Date(org.created_at), "d MMM yyyy") : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(org)}
                        data-testid={`edit-org-btn-${org.id}`}
                        className="rounded-sm text-xs"
                      >
                        <PencilSimple size={14} />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="org-dialog">
          <DialogHeader>
            <DialogTitle>{editingOrg ? "Edit Organization" : "New Organization"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="org-name" className="text-sm font-semibold text-slate-700">
                Organization Name
              </Label>
              <Input
                id="org-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Acme Engineering"
                data-testid="org-name-input"
                className="rounded-sm"
              />
            </div>

            <div className="flex items-center justify-between rounded-sm border border-slate-200 px-3 py-2.5">
              <div>
                <p className="text-sm font-semibold text-slate-800">Status</p>
                <p className="text-xs text-slate-500">
                  {formData.status === "active"
                    ? "Users in this org can access the system."
                    : "Users in this org will lose access (except super admins)."}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs font-semibold ${
                    formData.status === "active" ? "text-green-700" : "text-slate-400"
                  }`}
                >
                  Active
                </span>
                <Switch
                  checked={formData.status === "inactive"}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, status: checked ? "inactive" : "active" })
                  }
                  data-testid="org-status-switch"
                />
                <span
                  className={`text-xs font-semibold ${
                    formData.status === "inactive" ? "text-red-700" : "text-slate-400"
                  }`}
                >
                  Inactive
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeDialog}
              disabled={saving}
              data-testid="org-dialog-cancel-btn"
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="org-dialog-save-btn"
              className="rounded-sm"
            >
              {saving ? "Saving…" : editingOrg ? "Save Changes" : "Create Organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
